import React from "react";
import { isMobile, isSafari, isTablet } from "react-device-detect";
import ReactTooltip from "react-tooltip";
import styled, { css } from "styled-components";
import { TimerAlarmSoundsMap } from "@lifeat/shared/data-models/widget/Pomodoro/PomodoroWidgetSettingsEntity";
import { DEFAULT_ALARM_OBJ } from "@lifeat/shared/widget/PomodoroTimerConstants";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import LifeAtDropdownSelector from "@common/LifeAtDropdownSelector/LifeAtDropdownSelector";
import LifeAtDropdownSelectorElement from "@common/LifeAtDropdownSelector/LifeAtDropdownSelectorElement";
import LifeAtVolumeSlider from "@common/LifeAtVolumeSlider/LifeAtVolumeSlider";
import LifeAtVolumeToggle from "@common/LifeAtVolumeToggle/LifeAtVolumeToggle";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import { PomodoroService } from "../../../service/PomodoroService";
import {
    AUTO_TRANSITION_TIMER_LABEL,
    HIDE_POMODORO_COUNT_LABEL,
    LONG_BREAK_LABEL,
    POMODORO_LABEL,
    PRO_ALARM_CTA_TOOLTIP_LABEL,
    SAVE_TIMER_SETTINGS_BUTTON_LABEL,
    SHORT_BREAK_LABEL,
    TIMER_DURATION_MINS_MAX_VALUE,
    TIMER_DURATION_MINS_MIN_VALUE,
    TIMER_SOUND_LABEL,
    TIMER_VOL_LABEL,
} from "./TimerWidgetConstants";
import { PomodoroSettingsInput, PomodoroSettingsInputAction } from "./hooks/usePomodoroSettingsInput";

const SettingsRoot = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 20px;
`;

const SettingsGroup = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    border-top: 1px solid ${({ theme }) => theme.disabledColor};
    padding: 20px;
    gap: 20px;
`;

const SettingsBody = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const SoundWrapper = styled(SettingsBody)`
    justify-content: start;
    align-items: baseline;
`;

const SoundLabel = styled.div`
    white-space: nowrap;
`;

const VolumeWrapper = styled(SettingsBody)`
    align-items: center;
`;

const VolumeLabel = styled.div<{ hide: boolean }>`
    white-space: nowrap;
    ${({ hide }) =>
        hide &&
        css`
            display: none;
        `}
`;

const VolumeControl = styled.div`
    width: 100%;
    & .volume-slider-toggle {
        padding-left: 0px;
    }
    & .lifeat-volume-slider {
        padding: 0;
    }
`;

const CheckboxRow = styled.label`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 8px;
`;

const Checkbox = styled.input`
    height: 18px;
    width: 18px;
`;

const CustomSettingInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const CustomSettingInputLabel = styled.span`
    margin-bottom: 10px;
`;

const CustomSettingInput = styled.input`
    vertical-align: center;
    border-radius: 4px;
    height: 30px;
    width: 100px;
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    padding-left: 10px;
`;

const SaveButton = styled.span`
    width: 100%;
    & .lifeat-button {
        justify-content: center;
        border: none;
        background: ${({ theme }) => theme.textColorSpecial};
        color: white;

        * {
            background: unset;
        }
    }
`;

const SoundDropdown = styled(LifeAtDropdownSelector)<{ condensed: boolean; attach: boolean }>`
    margin-left: 20px;
    width: 100%;
    background-color: ${({ theme }) => theme.backgroundSpecial};

    .dropdown-selector-preview {
        justify-content: space-between;
    }

    .dropdown-selector-contents {
        ${({ condensed }) =>
            condensed &&
            css`
                max-height: 60px;
            `}
        ${({ attach }) =>
            attach &&
            css`
                position: relative;
            `}
    }

    .dropdown-selector-footer {
        display: none;
    }
`;

const SoundDropdownElement = styled.div<{ active: boolean; disabled: boolean }>`
    padding: 8px 10px;
    width: 100%;

    ${({ active }) =>
        active &&
        css`
            color: ${({ theme }) => theme.textColorSpecial};
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            color: ${({ theme }) => theme.textColorSecondary};
        `}
`;

interface TimerSettingsProps {
    canEditSettings: boolean;
    pomodoroSettingsInput: PomodoroSettingsInput;
    dispatch: React.Dispatch<PomodoroSettingsInputAction>;
    timerVolume: number;
    setTimerVolume: (newVol: number) => void;
    alarmSoundOptions: TimerAlarmSoundsMap;
    allowAutotransitionToggle: boolean;
    pomodoroService: PomodoroService;
    onSettingsSave?: () => void;
}

export default function TimerSettings({
    canEditSettings,
    pomodoroSettingsInput: {
        selectedAlarmSound,
        isAutoStartTimer,
        dataChanged,
        pomodoroTimerDuration,
        shortBreakTimerDuration,
        longBreakTimerDuration,
        hidePomodoroCount,
    },
    dispatch,
    timerVolume,
    setTimerVolume,
    alarmSoundOptions,
    allowAutotransitionToggle,
    pomodoroService,
    onSettingsSave,
}: TimerSettingsProps) {
    return (
        <SettingsRoot>
            {!canEditSettings && (
                <SettingsGroup>
                    {allowAutotransitionToggle && (
                        <CheckboxRow>
                            <Checkbox
                                type="checkbox"
                                id={"autoStartTimerToggle"}
                                name={"autoStartTimerToggle"}
                                defaultChecked={isAutoStartTimer}
                                onClick={() => dispatch({ type: "toggleIsAutoStartTimer" })}
                            />
                            {AUTO_TRANSITION_TIMER_LABEL}
                        </CheckboxRow>
                    )}
                    <CheckboxRow>
                        <Checkbox
                            type="checkbox"
                            id={"hidePomodoroCountToggle"}
                            name={"hidePomodoroCountToggle"}
                            defaultChecked={hidePomodoroCount}
                            onClick={() => dispatch({ type: "toggleHidePomodoroCount" })}
                        />
                        {HIDE_POMODORO_COUNT_LABEL}
                    </CheckboxRow>
                </SettingsGroup>
            )}
            <SettingsGroup>
                {!canEditSettings && (
                    <SettingsBody>
                        <CustomSettingInputContainer>
                            <CustomSettingInputLabel>{POMODORO_LABEL}</CustomSettingInputLabel>
                            <CustomSettingInput
                                id={"pomodoroTimerDurationInput"}
                                name={"pomodoroTimerDurationInput"}
                                type="number"
                                maxLength={3}
                                minLength={1}
                                max={TIMER_DURATION_MINS_MAX_VALUE}
                                min={TIMER_DURATION_MINS_MIN_VALUE}
                                autoComplete={"off"}
                                value={pomodoroTimerDuration}
                                onChange={(e) => dispatch({ type: "setPomodoroTimerDuration", value: e.target.value })}
                            />
                        </CustomSettingInputContainer>
                        <CustomSettingInputContainer>
                            <CustomSettingInputLabel>{SHORT_BREAK_LABEL}</CustomSettingInputLabel>
                            <CustomSettingInput
                                id={"shortBreakTimerDurationInput"}
                                name={"shortBreakTimerDurationInput"}
                                type="number"
                                maxLength={3}
                                minLength={1}
                                max={TIMER_DURATION_MINS_MAX_VALUE}
                                min={TIMER_DURATION_MINS_MIN_VALUE}
                                autoComplete={"off"}
                                value={shortBreakTimerDuration}
                                onChange={(e) =>
                                    dispatch({ type: "setShortBreakTimerDuration", value: e.target.value })
                                }
                            />
                        </CustomSettingInputContainer>
                        <CustomSettingInputContainer>
                            <CustomSettingInputLabel>{LONG_BREAK_LABEL}</CustomSettingInputLabel>
                            <CustomSettingInput
                                id={"longBreakTimerDurationInput"}
                                name={"longBreakTimerDurationInput"}
                                type="number"
                                maxLength={3}
                                minLength={1}
                                max={TIMER_DURATION_MINS_MAX_VALUE}
                                min={TIMER_DURATION_MINS_MIN_VALUE}
                                autoComplete={"off"}
                                value={longBreakTimerDuration}
                                onChange={(e) => dispatch({ type: "setLongBreakTimerDuration", value: e.target.value })}
                            />
                        </CustomSettingInputContainer>
                    </SettingsBody>
                )}
                {!canEditSettings && (
                    <SoundWrapper>
                        <SoundLabel>{TIMER_SOUND_LABEL}</SoundLabel>
                        <AlarmSoundSelector
                            selectedAlarmSound={selectedAlarmSound}
                            dispatch={dispatch}
                            alarmSoundOptions={alarmSoundOptions}
                            pomodoroService={pomodoroService}
                        />
                    </SoundWrapper>
                )}
                <VolumeWrapper>
                    <VolumeLabel hide={!(isMobile || isTablet)}>{TIMER_VOL_LABEL}</VolumeLabel>
                    <VolumeControl>
                        {isMobile || isTablet ? (
                            <LifeAtVolumeToggle volume={timerVolume} setVolume={setTimerVolume} />
                        ) : (
                            <LifeAtVolumeSlider volume={timerVolume} setVolume={setTimerVolume} />
                        )}
                    </VolumeControl>
                </VolumeWrapper>
                {dataChanged && (
                    <SaveButton>
                        <LifeAtButton
                            text={SAVE_TIMER_SETTINGS_BUTTON_LABEL}
                            isLink={false}
                            handleClick={onSettingsSave}
                        />
                    </SaveButton>
                )}
            </SettingsGroup>
        </SettingsRoot>
    );
}

interface AlarmSoundSelectorProps {
    selectedAlarmSound: string;
    dispatch: React.Dispatch<PomodoroSettingsInputAction>;
    alarmSoundOptions: TimerAlarmSoundsMap;
    pomodoroService: PomodoroService;
}

function AlarmSoundSelector({
    selectedAlarmSound,
    dispatch,
    alarmSoundOptions,
    pomodoroService,
}: AlarmSoundSelectorProps) {
    const { user, userData } = useUserContext();
    const { setSignInView, setUpgradeCtaContext } = useCallToActionContext();

    const handleSelectSound = (alarmId: string, isSoundDisabledPro: boolean) => {
        if (isSoundDisabledPro) {
            if (!user && !userData) {
                setSignInView(SignUpCTASource.POMODORO_SOUNDS);
            } else {
                setUpgradeCtaContext("pomodoroSounds", UpgradeCTASource.POMODORO_SOUND);
            }
        } else if (alarmId !== selectedAlarmSound) {
            dispatch({ type: "setSelectedAlarmSound", value: alarmId });
            pomodoroService.updateSelectedAlarmSrc?.(alarmId);
        }
    };

    return (
        <SoundDropdown
            condensed={isMobile || isTablet || isSafari}
            attach={isMobile && !isTablet}
            selectedElementId={selectedAlarmSound}
            selectedElementName={alarmSoundOptions[selectedAlarmSound].name}
        >
            {alarmSoundOptions !== undefined &&
                Object.keys(alarmSoundOptions).map((key) => {
                    const isSoundDisabledPro =
                        key !== Object.keys(DEFAULT_ALARM_OBJ)[0] &&
                        (userData === null || !userData?.proUserInfo?.isProUser);

                    return (
                        <LifeAtDropdownSelectorElement key={key}>
                            <SoundDropdownElement
                                active={selectedAlarmSound === key}
                                disabled={isSoundDisabledPro}
                                onClick={() => handleSelectSound(key, isSoundDisabledPro)}
                                data-tip={PRO_ALARM_CTA_TOOLTIP_LABEL}
                                data-for={"alarm-sounds-upgrade"}
                                data-tip-disable={!isSoundDisabledPro}
                            >
                                {alarmSoundOptions[key].name}
                            </SoundDropdownElement>
                            <ReactTooltip id={`alarm-sounds-upgrade`} />
                        </LifeAtDropdownSelectorElement>
                    );
                })}
        </SoundDropdown>
    );
}
